import React, { useEffect, useState, useCallback } from 'react';
import {
  activateAppUpdate,
  CHUNK_UPDATE_NEEDED_EVENT,
} from '../utils/appUpdate';

/**
 * "Update available" banner for long-lived tabs.
 *
 * The CRA/Workbox service worker keeps serving the previously cached bundle
 * until every tab of the app is closed, so users who keep the care app open
 * all day never receive deploys. index.js dispatches `sw-update-available`
 * (with the ServiceWorkerRegistration) when a new build finishes installing;
 * this banner lets the user activate it (SKIP_WAITING) and reload in place.
 */
export default function ServiceWorkerUpdateBanner() {
  const [notice, setNotice] = useState(
    () => {
      const chunkNotice =
        (typeof window !== 'undefined' && window.__chunkUpdateNotice) || null;
      if (chunkNotice) {
        return { ...chunkNotice, reason: 'chunk-error' };
      }
      const registration =
        (typeof window !== 'undefined' && window.__swWaitingRegistration) || null;
      return registration ? { registration, forceRecovery: false, reason: 'update' } : null;
    },
  );
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const onUpdate = (event) => {
      setNotice({
        registration: event.detail || null,
        forceRecovery: false,
        reason: 'update',
      });
    };
    const onChunkError = (event) => {
      setNotice({
        registration:
          event.detail?.registration || window.__swWaitingRegistration || null,
        forceRecovery: Boolean(event.detail?.forceRecovery),
        reason: 'chunk-error',
      });
    };

    window.addEventListener('sw-update-available', onUpdate);
    window.addEventListener(CHUNK_UPDATE_NEEDED_EVENT, onChunkError);
    return () => {
      window.removeEventListener('sw-update-available', onUpdate);
      window.removeEventListener(CHUNK_UPDATE_NEEDED_EVENT, onChunkError);
    };
  }, []);

  const handleRefresh = useCallback(() => {
    if (refreshing) return;
    setRefreshing(true);
    activateAppUpdate(notice?.registration, {
      forceRecovery: notice?.forceRecovery,
    }).catch(() => {
      setRefreshing(false);
    });
  }, [notice, refreshing]);

  if (!notice) return null;

  const isChunkError = notice.reason === 'chunk-error';

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        background: 'var(--tk-bg-0f172a)',
        color: '#f8fafc',
        padding: '0.6rem 1rem',
        borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.35)',
        fontSize: '0.875rem',
      }}
    >
      <span>
        {isChunkError
          ? 'The app was updated. Reload to continue.'
          : 'A new version of the app is available.'}
      </span>
      <button
        type="button"
        onClick={handleRefresh}
        disabled={refreshing}
        style={{
          background: '#38bdf8',
          color: 'var(--tk-text-0f172a)',
          border: 'none',
          borderRadius: '6px',
          padding: '0.35rem 0.9rem',
          fontWeight: 600,
          fontSize: '0.85rem',
          cursor: refreshing ? 'default' : 'pointer',
          opacity: refreshing ? 0.7 : 1,
        }}
      >
        {refreshing ? 'Updating…' : isChunkError ? 'Reload now' : 'Refresh'}
      </button>
    </div>
  );
}
